import {
  CdkConnectedOverlay,
  ScrollStrategyOptions,
} from '@angular/cdk/overlay';
import { DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { isAfter, isDate, parse } from 'date-fns';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'a-date-range-input',
  templateUrl: './a-date-range-input.component.html',
  styleUrls: ['./a-date-range-input.component.scss'],
  providers: [DatePipe],
})
export class ADateRangeInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() format: string = 'yyyy/MM/dd';
  @Input() startDate!: Date;
  @Input() endDate!: Date;
  @Input() width: string;
  @Input() hideLeftLabel: boolean = false;
  //such of Start of Month, Start of Year etc...
  @Input() startType!: string;
  //such of End of Month, End of Year etc...
  @Input() endType!: string;
  @Input('scrollStrategy') scrollStrategyValue: 'block' | 'none' | 'close' =
    'block';
  @Input() position: 'right' | 'left' = 'right';
  @Input() toolbarTemplate?: TemplateRef<any>;
  @Output() onCancel: EventEmitter<any> = new EventEmitter();
  @Output() onApply: EventEmitter<{ start: Date; end: Date; customData: any }> =
    new EventEmitter();
  @ViewChild('startInput', { static: true })
  startInput: ElementRef<HTMLInputElement>;
  @ViewChild('endInput', { static: true })
  endInput: ElementRef<HTMLInputElement>;
  @ViewChild(CdkConnectedOverlay, { static: false })
  connectedOverlay: CdkConnectedOverlay;
  formatFun = (date: Date) => {
    if (isDate(date) && !Number.isNaN(date.getTime())) {
      return this.datePipe.transform(new Date(date), this.format);
    }
    return '';
  };
  itdStartDate = new Date('2000/01/01');

  scrollStrategy: any;
  rightPosition = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 8,
    },
  ];
  leftPosition = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 8,
    },
  ];
  isOpenDateRangeSetting = false;
  startInvalidInput = false;
  endInvalidInput = false;
  dateRangeInvalid = false;
  startDateStr = '';
  endDateStr = '';
  startSubscription: Subscription;
  endSubscription: Subscription;
  overlaySubscription: Subscription;

  customData: any = {};

  constructor(
    private datePipe: DatePipe,
    private sstrategy: ScrollStrategyOptions,
    private cd: ChangeDetectorRef
  ) {
    //this.scrollStrategy = this.sstrategy.block();
  }
  ngOnDestroy(): void {
    this.startSubscription.unsubscribe();
    this.endSubscription.unsubscribe();
    this.overlaySubscription?.unsubscribe();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startDate'] || changes['endDate']) {
      this.updateDateStr();
      this.startInvalidInput = false;
      this.endInvalidInput = false;
      this.cd.detectChanges();
    }
  }

  ngOnInit(): void {
    switch (this.scrollStrategyValue) {
      case 'block':
        this.scrollStrategy = this.sstrategy.block();
        break;
      case 'none':
        this.scrollStrategy = this.sstrategy.noop();
        break;
      case 'close':
        this.scrollStrategy = this.sstrategy.close();
        break;
    }
    this.updateDateStr();
    this.startSubscription = fromEvent(this.startInput.nativeElement, 'keyup')
      .pipe(debounceTime(500))
      .subscribe(() => {
        let v = parse(
          this.startDateStr,
          this.format,
          new Date().setHours(0, 0, 0)
        );
        //#region requirement from Denis
        /**
         * I see what you’re saying. I wonder if we can implement some kind of logic where if the user types just the last two digits of a year “22” instead of “2022”,
         * for instance, the system would automatically convert that to “2022”. But if the user wants to type in “1922”,
         * we could still allow them to type all 4 digits? I feel like this is an edge case, and we want the most unintrusive solution possible.
         */
        const y4 = /[y]{4}/gi;
        if (Number.isNaN(v.getTime()) && !y4.test(this.format)) {
          let tmpFormat = this.format.replace('yy', 'yyyy');
          v = parse(this.startDateStr, tmpFormat, new Date().setHours(0, 0, 0));
        }
        //#endregion
        if (!Number.isNaN(v.getTime())) {
          if (this.checkDateRange(v, this.endDate)) {
            this.startDate = v;
            this.startInvalidInput = false;
            this.updateDateStr();
            this.onApply.emit({
              start: this.startDate,
              end: this.endDate,
              customData: this.customData,
            });
          }
        } else {
          this.startInvalidInput = true;
        }
      });
    this.endSubscription = fromEvent(this.endInput.nativeElement, 'keyup')
      .pipe(debounceTime(500))
      .subscribe(() => {
        let v = parse(
          this.endDateStr,
          this.format,
          new Date().setHours(0, 0, 0)
        );
        //#region requirement from Denis
        /**
         * I see what you’re saying. I wonder if we can implement some kind of logic where if the user types just the last two digits of a year “22” instead of “2022”,
         * for instance, the system would automatically convert that to “2022”. But if the user wants to type in “1922”,
         * we could still allow them to type all 4 digits? I feel like this is an edge case, and we want the most unintrusive solution possible.
         */
        const y4 = /[y]{4}/gi;
        if (Number.isNaN(v.getTime()) && !y4.test(this.format)) {
          let tmpFormat = this.format.replace('yy', 'yyyy');
          v = parse(this.endDateStr, tmpFormat, new Date().setHours(0, 0, 0));
        }
        //#endregion
        if (!Number.isNaN(v.getTime())) {
          if (this.checkDateRange(this.startDate, v)) {
            this.endDate = v;
            this.endInvalidInput = false;
            this.updateDateStr();
            this.onApply.emit({
              start: this.startDate,
              end: this.endDate,
              customData: this.customData,
            });
          }
        } else {
          this.endInvalidInput = true;
        }
      });
  }

  checkDateRange(start: Date, end: Date) {
    if (isAfter(start, end)) {
      this.dateRangeInvalid = true;
      return false;
    } else {
      this.dateRangeInvalid = false;
      return true;
    }
  }

  updateDateStr() {
    if (this.startDate === undefined) {
      this.startDate = this.itdStartDate;
    }
    this.startDateStr = this.formatFun(this.startDate);
    this.endDateStr = this.formatFun(this.endDate);
  }

  overlayOutsideClick() {
    this.isOpenDateRangeSetting = false;
  }
  onDateRangeSettingCancel() {
    this.isOpenDateRangeSetting = false;
  }

  onDateRangeApply(event: any) {
    this.customData = event.customData;
    this.onApply.emit(event);
    this.isOpenDateRangeSetting = false;
  }

  cancle() {
    this.onCancel.emit();
    this.isOpenDateRangeSetting = false;
  }

  onStartInputChange(event: any) {}

  overlayAttach() {
    //#region NOTE:Fixed the angular cdk(version 9.2.4) bug that is the detach event doesn't triggered when the cdkConnectedOverlay is closed via the scrollStrategy=close.
    if (this.scrollStrategyValue == 'close' && this.connectedOverlay) {
      this.overlaySubscription?.unsubscribe();
      this.overlaySubscription = this.connectedOverlay.overlayRef
        .detachments()
        .subscribe(() => {
          this.overlayOutsideClick();
          this.overlaySubscription.unsubscribe();
        });
    }
    //#endregion
  }
}
